/**
 * Resolve Basecamp integration tab ids from history entries and shorthand URL paths.
 * e.g. `flownotes-product/todos` -> tab id `basecamp-todos` (not a filesystem document).
 */

import type { OpenTab } from '@/components/features/document-tabs'
import type { WorkspaceRouteInfo } from '@/lib/app-routes'
import { workspaceIdForSlug } from '@/lib/app-routes'

export const BASECAMP_SECTION_PATH_SEGMENTS = new Set([
  'bookmarks',
  'mystuff',
  'todos',
  'projects',
  'activity',
  'triage',
])

export type BasecampStaticTabMeta = {
  type: OpenTab['type']
  title: string
  section?: string
}

export const STATIC_BASECAMP_TAB_META: Record<string, BasecampStaticTabMeta> = {
  'basecamp-todos': { type: 'integration-basecamp', title: 'Todos', section: 'todos' },
  'basecamp-projects': { type: 'integration-basecamp', title: 'Projects', section: 'projects' },
  'basecamp-bookmarks': { type: 'integration-basecamp', title: 'Bookmarks', section: 'bookmarks' },
  'basecamp-mystuff': { type: 'integration-basecamp', title: 'My Stuff', section: 'mystuff' },
  'basecamp-activity': { type: 'integration-basecamp', title: 'Activity', section: 'activity' },
  'basecamp-triage': { type: 'integration-basecamp-triage', title: 'Weekly Triage' },
  basecamp: { type: 'integration-basecamp', title: 'Basecamp' },
}

export type ResolvedBasecampTab = {
  tabId: string
  meta: BasecampStaticTabMeta
  workspaceSlug?: string
  workspaceId?: string
}

export function isBasecampIntegrationTabId(documentId: string | null | undefined): boolean {
  if (!documentId) return false
  // All Basecamp integration surfaces use tab ids `basecamp` or `basecamp-*`
  // (sections, projects, todos). Never treat these as document API ids.
  return documentId === 'basecamp' || documentId.startsWith('basecamp-')
}

/**
 * Discriminate Basecamp tab ids for history restore.
 * Order matters: `basecamp-todo-list-*` must not be treated as `basecamp-todo-*`.
 */
export type ParsedBasecampTabId =
  | { kind: 'static'; tabId: string; meta: BasecampStaticTabMeta }
  | { kind: 'project'; tabId: string; projectId: string }
  | { kind: 'todo-list'; tabId: string; listId: string }
  | { kind: 'todo'; tabId: string; todoId: string }
  | { kind: 'other'; tabId: string }

export function parseBasecampTabId(
  tabId: string | null | undefined,
): ParsedBasecampTabId | null {
  if (!tabId || !isBasecampIntegrationTabId(tabId)) return null
  const staticMeta = STATIC_BASECAMP_TAB_META[tabId]
  if (staticMeta) return { kind: 'static', tabId, meta: staticMeta }
  if (tabId.startsWith('basecamp-project-')) {
    return {
      kind: 'project',
      tabId,
      projectId: tabId.slice('basecamp-project-'.length),
    }
  }
  // Must run before basecamp-todo- (prefix overlap).
  if (tabId.startsWith('basecamp-todo-list-')) {
    return {
      kind: 'todo-list',
      tabId,
      listId: tabId.slice('basecamp-todo-list-'.length),
    }
  }
  if (tabId.startsWith('basecamp-todo-')) {
    return {
      kind: 'todo',
      tabId,
      todoId: tabId.slice('basecamp-todo-'.length),
    }
  }
  return { kind: 'other', tabId }
}

/** Shorthand `{workspaceSlug}/todos` or canonical tab id `basecamp-todos`. */
export function resolveBasecampTabFromShorthandPath(
  pathOrId: string,
  workspaces: WorkspaceRouteInfo[] = [],
): ResolvedBasecampTab | null {
  const trimmed = pathOrId.replace(/^\/+/, '').trim()
  if (!trimmed) return null

  const staticMeta = STATIC_BASECAMP_TAB_META[trimmed]
  if (staticMeta) {
    return { tabId: trimmed, meta: staticMeta }
  }

  const parts = trimmed.split('/').filter(Boolean)
  if (parts.length !== 2) return null

  const [slug, segment] = parts
  if (!BASECAMP_SECTION_PATH_SEGMENTS.has(segment)) return null
  if (workspaces.length > 0 && !workspaces.some((w) => w.slug === slug)) return null

  const tabId = segment === 'triage' ? 'basecamp-triage' : `basecamp-${segment}`
  const meta = STATIC_BASECAMP_TAB_META[tabId]
  if (!meta) return null

  return {
    tabId,
    meta,
    workspaceSlug: slug,
    workspaceId: workspaceIdForSlug(workspaces, slug),
  }
}

export function normalizeNavigationDocumentId(
  documentId: string,
  workspaces: WorkspaceRouteInfo[] = [],
): string {
  return resolveBasecampTabFromShorthandPath(documentId, workspaces)?.tabId ?? documentId
}

/** Mode-panel section ids that mirror BasecampSidebarPanel SECTIONS (+ triage/mystuff). */
export type BasecampModePanelSectionId =
  | 'bookmarks'
  | 'todos'
  | 'projects'
  | 'activity'
  | 'mystuff'
  | 'triage'

/**
 * Map the focused editor tab → Basecamp mode-panel section for active row chrome.
 * Index tabs (`basecamp-todos`, …) and nested todo/list tabs resolve to a section.
 */
export function resolveBasecampModePanelSection(
  tab: OpenTab | null | undefined,
): { sectionId: BasecampModePanelSectionId; organizationId?: string } | null {
  if (!tab) return null

  const organizationId =
    tab.integrationData?.organizationId ?? tab.workspaceId ?? undefined

  const fromSection = tab.integrationData?.section
  if (
    fromSection === 'bookmarks' ||
    fromSection === 'todos' ||
    fromSection === 'projects' ||
    fromSection === 'activity' ||
    fromSection === 'mystuff' ||
    fromSection === 'triage'
  ) {
    return { sectionId: fromSection, organizationId }
  }

  const staticMeta = STATIC_BASECAMP_TAB_META[tab.id]
  if (staticMeta?.section) {
    return {
      sectionId: staticMeta.section as BasecampModePanelSectionId,
      organizationId,
    }
  }

  if (tab.type === 'integration-basecamp-triage' || tab.id === 'basecamp-triage') {
    return { sectionId: 'triage', organizationId }
  }

  // Nested entity tabs: highlight the parent index section
  if (tab.type === 'integration-basecamp-todo') {
    return { sectionId: 'todos', organizationId }
  }
  if (tab.type === 'integration-basecamp-todo-list') {
    return { sectionId: 'projects', organizationId }
  }

  // Project home tabs often use id basecamp-project-{id} with section projects or none
  if (tab.type === 'integration-basecamp' && tab.integrationData?.projectId) {
    return { sectionId: 'projects', organizationId }
  }

  return null
}

export const BOOKMARKS_PROJECT_FOCUS_EVENT = 'basecamp-bookmarks-focus-project'
const BOOKMARKS_PROJECT_FOCUS_KEY = 'flownotes-bc-bookmarks-project-focus'

export type BookmarksProjectFocus = { id: string; name: string }

export function loadBookmarksProjectFocus(): BookmarksProjectFocus | null {
  try {
    const raw = sessionStorage.getItem(BOOKMARKS_PROJECT_FOCUS_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as BookmarksProjectFocus
    if (
      parsed &&
      typeof parsed.id === 'string' &&
      parsed.id &&
      typeof parsed.name === 'string'
    ) {
      return parsed
    }
  } catch {
    /* ignore */
  }
  return null
}

/** Persist (or clear) a project filter for the Bookmarks index. */
export function setBookmarksProjectFocus(
  project: BookmarksProjectFocus | null,
): void {
  try {
    if (project) {
      sessionStorage.setItem(BOOKMARKS_PROJECT_FOCUS_KEY, JSON.stringify(project))
    } else {
      sessionStorage.removeItem(BOOKMARKS_PROJECT_FOCUS_KEY)
    }
  } catch {
    /* ignore */
  }
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent(BOOKMARKS_PROJECT_FOCUS_EVENT, { detail: project }),
  )
}
